import { Divider, Group, ScrollArea, Stack } from '@mantine/core';
import { t } from 'i18next';
import { useEffect } from 'react';
import { type AllocationOfCostsChartResponseByCategory } from '../../../../api/financials-client/financials-client.type';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { FilterItem } from '../../../../components/filters/filter-item';
import { ResetFiltersButton } from '../../../../components/filters/reset-filters-button';
import { SmallSquareButton } from '../../../../components/small-square-button/small-square-button';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  resetAllocationOfCostsFilters,
  setAllocationOfCostsMeasure,
  setAllocationOfCostsOptions,
  setAllocationOfCostsTrendLines,
  toggleAllocationOfCostsOption,
  toggleAllocationOfCostsTrendLine,
} from '../../../../store/financials-store/financials-store.actions';
import type { AllocationOfCostsMeasures } from '../../../../store/financials-store/financials-store.type';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';
import { INITIAL_ALLOCATION_OF_COSTS_MEASURE, MAX_SELECTED_FILTERS } from '../../constants';
import { buildMeasureSelectOptions, maybeGetNullLabelForSeries } from './allocation-of-costs.helpers';
interface FiltersProps {
  setShow: (show: boolean) => void;
  data?: AllocationOfCostsChartResponseByCategory;
  isPending: boolean;
}

function Filters({ setShow, data, isPending }: FiltersProps) {
  const i18n = useTranslation();
  const {
    filters: {
      allocationOfCosts: { selectedMeasure, selectedOptions, selectedTrendLines },
    },
  } = useFinancialsStore();

  useEffect(() => {
    if (!selectedMeasure) {
      setAllocationOfCostsMeasure('assignee');
    }
  }, [selectedMeasure]);

  return (
    <Stack h="100%" style={{ position: 'relative' }}>
      <Group>
        <span style={{ flex: 1 }}>
          <Select
            size="xs"
            value={selectedMeasure}
            options={buildMeasureSelectOptions(data || ({} as AllocationOfCostsChartResponseByCategory))}
            placeholder="Select Cost Category"
            onChange={(value) => {
              setAllocationOfCostsMeasure(value as AllocationOfCostsMeasures);
              setAllocationOfCostsOptions([]);
              setAllocationOfCostsTrendLines([]);
            }}
          />
        </span>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack pb={40}>
          <Group justify="space-between">
            <Text size="small" weight="bold">
              {i18n.t('common.measures')}
            </Text>
            <Text size="small" weight="bold">
              {i18n.t('process_analysis.filters.trends')}
            </Text>
          </Group>
          {!isPending && data && selectedMeasure && selectedMeasure in data && data[selectedMeasure]?.options
            ? Object.keys(data[selectedMeasure].options)
                .sort((a, b) => {
                  // Try numeric comparison first for numeric field names
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB;
                  }
                  // Fall back to string comparison for non-numeric fields
                  return a.localeCompare(b);
                })
                .map((option) => (
                  <FilterItem
                    key={option}
                    value={option}
                    label={maybeGetNullLabelForSeries(option)}
                    isSelected={selectedOptions.includes(option)}
                    isSelectedTrends={selectedTrendLines.includes(option)}
                    showTrends={true}
                    selectedCount={selectedOptions.length}
                    maxSelected={MAX_SELECTED_FILTERS}
                    onToggle={toggleAllocationOfCostsOption}
                    onTrendsToggle={toggleAllocationOfCostsTrendLine}
                  />
                ))
            : !isPending && <ContentPlaceholder icon="event_busy" text={t('financials.no_filtered_data')} />}
          <Divider />
        </Stack>
      </ScrollArea>
      <ResetFiltersButton
        onClick={() => resetAllocationOfCostsFilters()}
        visible={
          selectedMeasure !== INITIAL_ALLOCATION_OF_COSTS_MEASURE ||
          selectedOptions.length > 0 ||
          selectedTrendLines.length > 0
        }
      />
    </Stack>
  );
}

export { Filters };
